import { onlyNumeric } from '@origin-digital/normalize';
import * as React from 'react';
import { Box, Button, Heading3 } from '@origin-digital/style-guide';
import { connect } from 'react-redux';
import { Form, FieldArray, Field, FieldArrayRenderProps } from 'formik';

import { contact } from '../../../content.json';

import { RequestConsultationFieldsProps } from './RequestConsultationFields.types';

import DisqualificationProvider from '../../../../../components/DisqualificationProvider';
import { DisqualificationContext } from '../../../../../components/DisqualificationProvider/disqualification-context';
import {
  AddressField,
  HiddenField,
  RadioImageGroupField,
  ManualAddress,
  TextField
} from '../../../../../components/Form/Fields';
import DisqualificationAlert from '../../../../../components/DisqualificationAlert';
import FormRow from '../../../../../components/Form/Layout/FormRow';

import { CHECK_SERVICEABILITY } from '../../../../../graphql/queries';
import analytics from '../../../../../analytics';
import { isInitialValid } from '../../../../../utils/formHelpers';
import { checkIfPostcodeServiceable } from '../../../../../redux/thunks';
import { updateCustomerDetail } from '../../../../../redux/actions/customer';
import { isFieldTouched } from '../../../../../utils/formHelpers';
import { StoreState } from '../../../../../redux/store.types';
import { getCustomer, getDisqualification, getRequest } from '../../../../../redux/selectors';

export const RequestConsultationFields = (props: RequestConsultationFieldsProps): JSX.Element => {
  const {
    isValid,
    productType,
    validating,
    asyncSubmitting,
    customerAddress,
    disqualifiedFields,
    hasManualAddress,
    checkIfPostcodeServiceable,
    updateCustomerDetail
  } = props;

  return (
    <Form>
      <DisqualificationProvider disqualificationMessages={contact.content.disqualification}>
        <DisqualificationContext.Consumer>
          {context => (
            <React.Fragment>
              <Heading3 text={contact.content.heading3} marginBottom={true} />
              <FormRow>
                <Field
                  name={contact.fields.address.name}
                  component={AddressField}
                  label={contact.fields.address.label}
                  helpText={contact.fields.address.helpText}
                  productType={productType}
                  customerAddress={customerAddress}
                  isInitialValid={isInitialValid}
                  isDisqualified={disqualifiedFields.includes(contact.fields.hiddenPostcode.name)}
                  hasManualAddress={hasManualAddress}
                  manualAddressContent={contact}
                  manualAddressPlaceholder={contact.content.address.manualAddressPlaceholder}
                  graphQuery={CHECK_SERVICEABILITY}
                  validating={validating}
                  changeHandler={updateCustomerDetail}
                  addressCheckHandler={checkIfPostcodeServiceable}
                />
                <ManualAddress
                  formData={contact}
                  productType={productType}
                  changeHandler={updateCustomerDetail}
                  isInitialValid={isInitialValid}
                  hasManualAddress={hasManualAddress}
                  addressCheckHandler={checkIfPostcodeServiceable}
                  isDisqualified={disqualifiedFields.includes(contact.fields.hiddenPostcode.name)}
                  validating={validating}
                  graphQuery={CHECK_SERVICEABILITY}
                />
                <Field
                  name={contact.fields.hiddenPostcode.name}
                  render={(helpers: FieldArrayRenderProps) => {
                    const touched: boolean = isFieldTouched(helpers.form, name);
                    return (
                      <React.Fragment>
                        <HiddenField
                          name={contact.fields.hiddenPostcode.name}
                          touched={touched}
                          helpers={helpers}
                          disqualificationHandler={context.disqualificationHandler}
                        />
                        <Box marginTop={-3}>
                          <DisqualificationAlert
                            name={contact.fields.hiddenPostcode.name}
                            heading={contact.content.disqualification.heading}
                            message={contact.content.disqualification.hiddenPostcode}
                          />
                        </Box>
                      </React.Fragment>
                    );
                  }}
                />
              </FormRow>
              <FormRow
                content={contact.content.propertyType}
                byline={contact.content.propertyType.byline}
              >
                <FieldArray
                  name={contact.fields.propertyType.name}
                  render={(helpers: FieldArrayRenderProps) => {
                    const touched: boolean = isFieldTouched(helpers.form, name);

                    return (
                      <React.Fragment>
                        <RadioImageGroupField
                          name={contact.fields.propertyType.name}
                          touched={touched}
                          options={contact.fields.propertyType.options}
                          helpers={helpers}
                          changeHandler={updateCustomerDetail}
                          isDisqualified={disqualifiedFields.includes(
                            contact.fields.propertyType.name
                          )}
                          disqualificationHandler={context.disqualificationHandler}
                        />
                        <DisqualificationAlert
                          name={contact.fields.propertyType.name}
                          heading={contact.content.disqualification.headingPropertyType}
                          message={contact.content.disqualification.propertyType}
                          link={contact.content.disqualification.links}
                        />
                      </React.Fragment>
                    );
                  }}
                />
              </FormRow>

              <FormRow content={contact.content.roofType} byline={contact.content.roofType.byline}>
                <FieldArray
                  name={contact.fields.roofType.name}
                  render={(helpers: FieldArrayRenderProps) => {
                    const touched: boolean = isFieldTouched(helpers.form, name);

                    return (
                      <React.Fragment>
                        <RadioImageGroupField
                          name={contact.fields.roofType.name}
                          touched={touched}
                          options={contact.fields.roofType.options}
                          helpers={helpers}
                          changeHandler={updateCustomerDetail}
                          isDisqualified={disqualifiedFields.includes(contact.fields.roofType.name)}
                          disqualificationHandler={context.disqualificationHandler}
                        />
                        <DisqualificationAlert
                          name={contact.fields.roofType.name}
                          heading={contact.content.disqualification.heading}
                          message={contact.content.disqualification.roofType}
                        />
                      </React.Fragment>
                    );
                  }}
                />
              </FormRow>
              <Field
                name={contact.fields.phone.name}
                component={TextField}
                helpText={contact.fields.phone.helpText}
                isInitialValid={isInitialValid}
                label={contact.fields.phone.label}
                transform={onlyNumeric}
                changeHandler={updateCustomerDetail}
                maxLength={10}
                analytics={false}
              />
              <Box marginTop={10}>
                <Button
                  type="submit"
                  block={true}
                  data-id="RequestACall"
                  text={contact.content.buttons.submit}
                  disabled={!isValid || validating}
                  spinner={asyncSubmitting}
                  onClick={() => {
                    analytics.buttonClick('next', 'Request a call', 'step1');
                  }}
                />
              </Box>
            </React.Fragment>
          )}
        </DisqualificationContext.Consumer>
      </DisqualificationProvider>
    </Form>
  );
};

const mapStateToProps = (state: StoreState) => {
  const customer = getCustomer(state);
  const request = getRequest(state);
  const disqualification = getDisqualification(state);
  return {
    productType: customer.productType,
    validating: request.validating,
    asyncSubmitting: request.submitting,
    disqualifiedFields: disqualification.disqualifiedFields,
    hasManualAddress: customer.hasManualAddress ? customer.hasManualAddress : false,
    customerAddress: {
      label: customer.address,
      qasAddress: {
        unit: customer.unit ? customer.unit : '',
        streetNumber: customer.streetNumber ? customer.streetNumber : '',
        streetName: customer.streetName ? customer.streetName : '',
        streetType: customer.streetType ? customer.streetType : '',
        streetNameOnly: customer.streetNameOnly ? customer.streetNameOnly : '',
        suburb: customer.suburb ? customer.suburb : '',
        city: customer.city ? customer.city : '',
        state: customer.state ? customer.state : '',
        postcode: customer.postcode ? customer.postcode : '',
        hiddenPostcode: customer.hiddenPostcode ? customer.hiddenPostcode : 'true',
        label: customer.address ? customer.address : ''
      }
    }
  };
};

export default connect(
  mapStateToProps,
  { checkIfPostcodeServiceable, updateCustomerDetail }
)(RequestConsultationFields);
